import React, { useRef, useEffect, useCallback, useState } from 'react';
import AreaTitle from '../../../../components/areaTitle/AreaTitle';
import DateRangeHeader from '../../../../components/dateRangeHeader/DateRangeHeader';
import { DataProviderInstance } from '../../../../api/DataProvider';
import ListItem from './list/ListItem';
import styles from './Financial.module.css';
import FilterList from '../../filters/FilterList';
import Loading from '../../../../components/loading/Loading';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { appointmentProcedureIds } from '../../../appointments/v2/utils/appointmentUtils';

const Financial = () => {

    const { CompanyConfig, GetReportsFinancial, GetPatientById } = DataProviderInstance();

    // Índices 0-1: filtros de faturamento (checkbox multi-seleção)
    const HAS_INVOICE            = "Faturadas"
    const NEEDS_DOCUMENT_INVOICE = "Faturadas e pediram nota fiscal"

    // Índices 2-5: filtros de status fiscal (checkbox multi-seleção, OR logic)
    const FISCAL_PENDING    = "NF: Pendentes"
    const FISCAL_ISSUED     = "NF: Emitidas"
    const FISCAL_PROCESSING = "NF: Em processamento"
    const FISCAL_ERROR      = "NF: Com erro"

    // Índices dos filtros fiscais no array
    const FISCAL_PENDING_IDX    = 2;
    const FISCAL_ISSUED_IDX     = 3;
    const FISCAL_PROCESSING_IDX = 4;
    const FISCAL_ERROR_IDX      = 5;

    const filters = [
        HAS_INVOICE,            // 0
        NEEDS_DOCUMENT_INVOICE, // 1
        FISCAL_PENDING,         // 2
        FISCAL_ISSUED,          // 3
        FISCAL_PROCESSING,      // 4
        FISCAL_ERROR,           // 5
    ];

    const [list, setList] = React.useState([]);
    const [filtered, setFiltered] = React.useState([]);
    const [currentFilters, setCurrentFilters] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [filtersByResource, setFiltersByResource] = useState([]);
    const [filtersByProcedure, setFiltersByProcedure] = useState([]);

    const activeResources = CompanyConfig?.Resources?.filter(r => r.Active == 1) || [];
    const activeProcedures = CompanyConfig?.Procedures?.filter(p => p.Active == 1) || [];
    const resourceLabels = activeResources.map(r => r.Label);
    const procedureLabels = activeProcedures.map(p => p.Label);

    const [startDate, setStartDate] = React.useState(null);
    const [endDate, setEndDate] = React.useState(null);

    const [total, setTotal] = React.useState(0);
    const [totalIssued, setTotalIssued] = React.useState(0);

    const filterRef = useRef(null);

    /**
     * Resolve o fiscalStatus de um item para fins de filtro e contagem.
     * - Se o item tem nota ativa (queued/processing/issued/error/canceled) → usa esse status
     * - Se o item precisa de nota mas não tem nenhuma registrada → 'pending'
     * - Caso contrário → null
     */
    const resolveFiscalStatus = (item) => {
        const status = item.fiscalStatus;
        const needsInvoice = item.invoice && item.invoice.issueDocument == "1";

        if (status === 'issued' || status === 'processing' || status === 'queued' || status === 'error' || status === 'canceled') {
            return status;
        }
        if (needsInvoice) return 'pending';
        return null;
    };

    /**
     * Aplica todos os filtros sobre a lista e atualiza `filtered`.
     * Recebe lista e filtros como parâmetros para evitar stale closure.
     */
    const applyFilters = useCallback((sourceList, activeFilters) => {
        let result = [...sourceList];

        // Filtro 0 (Faturadas): mostra apenas itens com invoice
        if (activeFilters.includes(0)) {
            result = result.filter(item => item.invoice);
        }

        // Filtro 1 (Faturadas e pediram NF): mostra apenas itens com issueDocument=1
        if (activeFilters.includes(1)) {
            result = result.filter(item => item.invoice && item.invoice.issueDocument == "1");
        }

        // Filtros fiscais 2-5: OR logic — se nenhum ativo, mostra tudo
        const hasPendingFilter    = activeFilters.includes(FISCAL_PENDING_IDX);
        const hasIssuedFilter     = activeFilters.includes(FISCAL_ISSUED_IDX);
        const hasProcessingFilter = activeFilters.includes(FISCAL_PROCESSING_IDX);
        const hasErrorFilter      = activeFilters.includes(FISCAL_ERROR_IDX);
        const anyFiscalActive     = hasPendingFilter || hasIssuedFilter || hasProcessingFilter || hasErrorFilter;

        if (anyFiscalActive) {
            result = result.filter(item => {
                const resolved = resolveFiscalStatus(item);
                if (hasPendingFilter && resolved === 'pending') return true;
                if (hasIssuedFilter && resolved === 'issued') return true;
                if (hasProcessingFilter && (resolved === 'processing' || resolved === 'queued')) return true;
                if (hasErrorFilter && (resolved === 'error' || resolved === 'canceled')) return true;
                return false;
            });
        }

        // Filtro por resource
        if (filtersByResource.length > 0 && filtersByResource.length < resourceLabels.length) {
            result = result.filter((item) => {
                const resourceIdx = activeResources.findIndex(r => r.Id == item.resource);
                return filtersByResource.includes(resourceIdx);
            });
        }

        // Filtro por procedure
        if (filtersByProcedure.length > 0 && filtersByProcedure.length < procedureLabels.length) {
            // Vários procedimentos por atendimento: entra se qualquer um estiver no filtro
            result = result.filter((item) => appointmentProcedureIds(item).some((id) => {
                const procedureIdx = activeProcedures.findIndex(p => String(p.Id) === id);
                return filtersByProcedure.includes(procedureIdx);
            }));
        }

        setFiltered(result);
        calculateTotal(result);
    }, [filtersByResource, filtersByProcedure, activeResources, activeProcedures, resourceLabels.length, procedureLabels.length]);

    // Recalcula filtered quando filtros mudam
    useEffect(() => {
        applyFilters(list, currentFilters);
    }, [currentFilters, list, filtersByResource, filtersByProcedure]);

    const calculateTotal = useCallback((sourceList) => {
        let t = 0;
        let ti = 0;
        sourceList.forEach(item => {
            t += Number(item.invoice?.price) || 0;
            if (item.invoice?.issueDocument == "1") {
                ti += Number(item.invoice.price);
            }
        });
        setTotal(t);
        setTotalIssued(ti);
    }, []);

    const handleDateChange = (initialDate, endDate) => {
        setStartDate(initialDate);
        setEndDate(endDate);
        loadData(initialDate, endDate);
    }

    const loadData = (initialDate, endDate) => {
        setIsLoading(true);
        GetReportsFinancial(initialDate, endDate, (response) => {
            const data = response.data || [];
            setList(data);
            setIsLoading(false);
        });
    }

    function renderItem(item, index) {
        const patientData = GetPatientById(item.patient);

        return (
            <ListItem
                key={item.id ?? index}
                id={item.id}
                date={item.date}
                name={patientData?.name}
                cpf={patientData?.cpf}
                invoiceData={item.invoice}
                appointmentData={{
                    id: item.id,
                    date: item.date,
                    price: item.invoice?.price,
                    procedure: item.procedure,
                    procedures: item.procedures,
                    invoiceId: item.invoice?.id,
                }}
                patientData={patientData}
                fiscalStatus={item.fiscalStatus ?? null}
            />
        );
    }

    const filterChangeHandler = (activeFilters) => {
        setCurrentFilters(activeFilters);
    }

    // Contadores fiscais calculados diretamente da lista completa
    const fiscalCounters = React.useMemo(() => {
        let issued = 0;
        let pending = 0;
        let processing = 0;
        let error = 0;

        list.forEach(item => {
            const resolved = resolveFiscalStatus(item);
            if (resolved === 'issued') issued++;
            else if (resolved === 'processing' || resolved === 'queued') processing++;
            else if (resolved === 'error' || resolved === 'canceled') error++;
            else if (resolved === 'pending') pending++;
        });

        return { issued, pending, processing, error };
    }, [list]);

    const totalStr = total.toFixed(2).replace('.', ',');
    const totalIssuedStr = totalIssued.toFixed(2).replace('.', ',');

    const handleVerPendentes = () => {
        if (filterRef.current) {
            filterRef.current.addCheckedItem(FISCAL_PENDING_IDX);
        }
    };

    return (
        <div className={styles.mainContainer}>
            <AreaTitle Title="Financeiro" />
            <DateRangeHeader onChange={handleDateChange} />
            <div className={styles.filtersWrapper}>
                <FilterList
                    ref={filterRef}
                    id="reportsFinancialV3"
                    label="Faturamento / Nota Fiscal"
                    filters={filters}
                    onChange={filterChangeHandler}
                    initOpened={true}
                    defaultChecked={[]}
                />
                {resourceLabels.length > 0 && (
                    <FilterList
                        key={`res-${resourceLabels.length}`}
                        id="reportsFinancialResources"
                        label="Profissional"
                        filters={resourceLabels}
                        onChange={(items) => setFiltersByResource(items)}
                        initOpened={true}
                        defaultChecked={resourceLabels.map((_, i) => i)}
                    />
                )}
                {procedureLabels.length > 0 && (
                    <FilterList
                        key={`proc-${procedureLabels.length}`}
                        id="reportsFinancialProcedures"
                        label="Procedimento"
                        filters={procedureLabels}
                        onChange={(items) => setFiltersByProcedure(items)}
                        initOpened={true}
                        defaultChecked={procedureLabels.map((_, i) => i)}
                    />
                )}
            </div>
            {isLoading && <Loading />}

            {/* Painel de resumo com cards de contadores fiscais */}
            <div className={styles.summaryPanel}>
                <div className={styles.summaryCards}>
                    <div className={styles.summaryCard}>
                        <AttachMoneyIcon className={styles.summaryCardIcon} style={{ color: '#1976d2' }} />
                        <div className={styles.summaryCardContent}>
                            <span className={styles.summaryCardLabel}>Total faturado</span>
                            <span className={styles.summaryCardValue}>R$ {totalStr}</span>
                        </div>
                    </div>

                    <div className={styles.summaryCard}>
                        <ReceiptLongIcon className={styles.summaryCardIcon} style={{ color: '#1976d2' }} />
                        <div className={styles.summaryCardContent}>
                            <span className={styles.summaryCardLabel}>Com nota fiscal</span>
                            <span className={styles.summaryCardValue}>R$ {totalIssuedStr}</span>
                        </div>
                    </div>

                    <div className={styles.summaryCard}>
                        <CheckCircleOutlineIcon className={styles.summaryCardIcon} style={{ color: '#2e7d32' }} />
                        <div className={styles.summaryCardContent}>
                            <span className={styles.summaryCardLabel}>Notas emitidas</span>
                            <Chip
                                label={fiscalCounters.issued}
                                size="small"
                                style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }}
                            />
                        </div>
                    </div>

                    <div className={styles.summaryCard}>
                        <HourglassEmptyIcon className={styles.summaryCardIcon} style={{ color: '#ed6c02' }} />
                        <div className={styles.summaryCardContent}>
                            <span className={styles.summaryCardLabel}>Em processamento</span>
                            <Chip
                                label={fiscalCounters.processing}
                                size="small"
                                style={{ backgroundColor: '#fff3e0', color: '#ed6c02', fontWeight: 600 }}
                            />
                        </div>
                    </div>

                    <div className={styles.summaryCard}>
                        <ErrorOutlineIcon className={styles.summaryCardIcon} style={{ color: '#d32f2f' }} />
                        <div className={styles.summaryCardContent}>
                            <span className={styles.summaryCardLabel}>Com erro</span>
                            <Chip
                                label={fiscalCounters.error}
                                size="small"
                                style={{ backgroundColor: '#ffebee', color: '#d32f2f', fontWeight: 600 }}
                            />
                        </div>
                    </div>

                    <div className={styles.summaryCard}>
                        <PendingActionsIcon className={styles.summaryCardIcon} style={{ color: '#9c27b0' }} />
                        <div className={styles.summaryCardContent}>
                            <span className={styles.summaryCardLabel}>Pendentes</span>
                            <Chip
                                label={fiscalCounters.pending}
                                size="small"
                                style={{ backgroundColor: '#f3e5f5', color: '#9c27b0', fontWeight: 600 }}
                            />
                        </div>
                    </div>
                </div>

                {/* Alerta de pendentes */}
                {fiscalCounters.pending > 0 && (
                    <Alert
                        severity="warning"
                        icon={<WarningAmberIcon />}
                        className={styles.pendingAlert}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={handleVerPendentes}
                                style={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                            >
                                Ver pendentes
                            </Button>
                        }
                    >
                        Existem <strong>{fiscalCounters.pending}</strong> {fiscalCounters.pending === 1 ? 'nota pendente' : 'notas pendentes'} de emissão.
                    </Alert>
                )}
            </div>

            <div className={styles.containerTable}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Data</th>
                            <th>Paciente</th>
                            <th>CPF</th>
                            <th>Valor</th>
                            <th>Faturado</th>
                            <th>Emitir nota fiscal?</th>
                            <th>NF emitida</th>
                            <th>Nota Fiscal</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((item, index) => renderItem(item, index))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Financial;
